import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    /**
     * Close the drawer when checkout / wallets are activated, without breaking Razorpay or
     * form submit: use `click` (capture) + `queueMicrotask` so the browser finishes the click
     * and default actions before `closeDialog` runs. `pointerdown` can run too early and
     * interfere with Smart Checkout. `composedPath` includes Shadow DOM (accelerated wallets).
     * `submit` (capture) covers keyboard submit on the checkout button.
     */
    this.addEventListener('click', this.#handleCheckoutAreaClick, true);
    this.addEventListener('submit', this.#handleCartFormSubmit, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleCheckoutAreaClick, true);
    this.removeEventListener('submit', this.#handleCartFormSubmit, true);
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /** @param {MouseEvent} event */
  #handleCheckoutAreaClick = (event) => {
    if (!this.refs.dialog?.open) return;
    if (event.button !== 0) return;

    for (const node of event.composedPath()) {
      if (!(node instanceof Element) || !this.contains(node)) continue;

      if (
        node.classList?.contains('cart__ctas') ||
        node.classList?.contains('additional-checkout-buttons') ||
        node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART'
      ) {
        queueMicrotask(() => this.closeDialog());
        return;
      }
    }
  };

  /** @param {SubmitEvent} event */
  #handleCartFormSubmit = (event) => {
    if (!this.refs.dialog?.open) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form' || !this.contains(form)) {
      return;
    }
    const sub = event.submitter;
    if (sub instanceof HTMLButtonElement && sub.name === 'checkout') {
      void this.closeDialog();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
